/**
 * Workspace Header Component
 * Header for the project workspace with project info and actions
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  MoreVertical, 
  Edit,
  Trash2,
  Share2,
  Image,
  AlertTriangle,
  X,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { type DBProject } from '../../db';
import { StatusBadge } from '../ui/Badge';
import { Progress } from '../ui/Progress';
import { projectsApi } from '../../lib/api';

interface WorkspaceHeaderProps {
  project: DBProject;
  onProjectUpdate?: () => void;
}

export function WorkspaceHeader({ project, onProjectUpdate }: WorkspaceHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: project.name,
    code: project.code,
    status: project.status,
  });
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete project "${project.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await projectsApi.delete(project.id);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project', {
        description: 'Please try again',
      });
    } finally {
      setIsDeleting(false);
      setMenuOpen(false);
    }
  };

  const handleEditOpen = () => {
    setEditForm({
      name: project.name,
      code: project.code,
      status: project.status,
    });
    setIsEditModalOpen(true);
    setMenuOpen(false);
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      await projectsApi.update(project.id, editForm);
      toast.success('Project updated successfully');
      setIsEditModalOpen(false);
      onProjectUpdate?.();
      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project', {
        description: 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Aviation status badge
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'EXPORTED':
        return 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/30';
      case 'IN_PROGRESS':
        return 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
      case 'PENDING':
      case 'DRAFT':
        return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';
      default:
        return 'bg-slate-100 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/30';
    }
  };

  return (
    <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700/50 px-6 py-4 transition-colors">
      <div className="flex items-center justify-between">
        {/* Left side - Back button and project info */}
        <div className="flex items-center gap-4">
          <Link
            to="/projects"
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
            title="Back to projects"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </Link>
          
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-mono font-bold text-slate-800 dark:text-slate-100 tracking-wide">{project.code}</h1>
              <span className={`px-2 py-0.5 text-[10px] font-mono font-medium uppercase tracking-wider rounded border ${getStatusStyle(project.status)}`}>
                {project.status.replace('_', ' ')}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm font-mono text-slate-500">
              <span>{project.name}</span>
              {project.surveyDate && (
                <>
                  <span className="text-slate-400 dark:text-slate-600">•</span>
                  <span>
                    Inspection: {format(new Date(project.surveyDate), "MMM d, yyyy")}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Stats and actions */}
        <div className="flex items-center gap-6">
          {/* Quick stats */}
          <div className="flex items-center gap-4 text-sm font-mono">
            <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
              <Image className="w-4 h-4" />
              <span className="font-medium text-slate-700 dark:text-slate-200">{project.photoCount}</span>
              <span>photos</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium text-slate-700 dark:text-slate-200">{project.anomalyCount}</span>
              <span>anomalies</span>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 w-32">
            <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all" 
                style={{ width: `${project.progress}%` }}
              />
            </div>
            <span className="text-sm font-mono font-medium text-slate-500 dark:text-slate-400 w-10 tabular-nums">
              {project.progress}%
            </span>
          </div>

          {/* Export button */}
          <button
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors font-mono text-sm border border-slate-200 dark:border-slate-600/50"
          >
            <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span>Export</span>
          </button>

          {/* More menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <MoreVertical className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </button>

            {menuOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setMenuOpen(false)} 
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-800/95 backdrop-blur rounded-lg shadow-lg border border-slate-200 dark:border-slate-700/50 z-50 py-1">
                  <button 
                    onClick={handleEditOpen}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm font-mono text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Details
                  </button>
                  <button 
                    onClick={() => {
                      toast.info('Share feature coming soon');
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm font-mono text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  <hr className="my-1 border-slate-200 dark:border-slate-700/50" />
                  <button 
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm font-mono text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting...' : 'Delete Project'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Details Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-700/50">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700/50 flex items-center justify-between">
              <h3 className="font-mono font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Edit Project Details
              </h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Project Code */}
              <div>
                <label className="block text-xs font-mono text-slate-500 uppercase tracking-wider mb-2">
                  Project Code
                </label>
                <input
                  type="text"
                  value={editForm.code}
                  onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg font-mono text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  placeholder="Project code"
                />
              </div>

              {/* Project Name */}
              <div>
                <label className="block text-xs font-mono text-slate-500 uppercase tracking-wider mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg font-mono text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  placeholder="Project name"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-mono text-slate-500 uppercase tracking-wider mb-2">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg font-mono text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="EXPORTED">Exported</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700/50 flex justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 font-mono text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-600/80 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-mono border border-blue-500 dark:border-blue-500/50 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
